import type { ComponentScores } from '../../data/types';
import { formatScore } from '../../lib/format';
import styles from './ComponentBreakdown.module.css';

interface ComponentBreakdownProps {
  scores: ComponentScores;
}

interface Row {
  key: keyof ComponentScores;
  label: string;
  description: string;
}

const rows: Row[] = [
  { key: 'overall',      label: 'Overall sentiment', description: 'general tone of the discussion' },
  { key: 'vibe',         label: 'Vibe',              description: 'reception, taste, day-to-day feel' },
  { key: 'quality',      label: 'Quality',           description: 'output fidelity, smartness, follow-through' },
  { key: 'reliability',  label: 'Reliability',       description: 'uptime, regression complaints, behavioural drift' },
  { key: 'price_value',  label: 'Cost perception',   description: 'whether people think it is worth what it costs' },
  { key: 'usage_intent', label: 'Usage intent',      description: 'how often people say they actually use it' },
];

export function ComponentBreakdown({ scores }: ComponentBreakdownProps) {
  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Component breakdown</p>
        <h2 className={styles.heading}>Six dimensions, on a 0&ndash;100 scale.</h2>
      </header>

      <ol className={styles.rows}>
        {rows.map((row) => {
          const value = scores[row.key];
          return (
            <li key={row.key} className={styles.row}>
              <div className={styles.label}>
                <span className={styles.labelName}>{row.label}</span>
                <span className={styles.labelDesc}>{row.description}</span>
              </div>
              <div className={styles.bar}>
                <div className={styles.barTrack} />
                <div
                  className={styles.barFill}
                  style={{ ['--fill' as string]: `${value}%` }}
                />
                <div
                  className={styles.barMidline}
                  aria-label="midpoint"
                  title="50 — neither positive nor negative"
                />
              </div>
              <div className={styles.value}>{formatScore(value)}</div>
            </li>
          );
        })}
      </ol>

      <p className={styles.caption}>
        50 is the neutral midpoint. Anything above is reception that leans positive on the dimension;
        anything below leans negative.
      </p>
    </section>
  );
}
